import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { map } from "rxjs/operators";

import {
  CANCEL_PAYMENT,
  CANCEL_SUBMISSION,
  COPY_SUBMISSION,
  EPAYMENT_ACKNOWLEDGE,
  GET_AGENT_E_INVOICE_LIST,
  GET_AGENTS_LIST,
  GET_ESUBMISSION_AGENT_E_INVOICE_SEARCH_BY,
  GET_ESUBMISSION_AGENT_E_INVOICE_TYPES,
  GET_ESUBMISSION_BANK_NAMES,
  GET_ESUBMISSION_CBC_OUTSTANDING_DAYS,
  GET_ESUBMISSION_DOCUMENT_TYPE,
  GET_ESUBMISSION_ENQUIRY_SEARCH_BY,
  GET_ESUBMISSION_PAYMENT_ENQUIRY_SEARCH_BY,
  GET_ESUBMISSION_PAYMENT_MODE,
  GET_ESUBMISSION_PAYMENT_STATUS,
  GET_ESUBMISSION_PRODUCT_TYPES,
  GET_ESUBMISSION_SEARCH_BY,
  GET_ESUBMISSION_SUBMIT_STATUS,
  GET_OUTSTANDING_VIEW,
  GET_PAYMENT_CONFIG,
  GET_PAYMENT_ENQUIRY_LIST,
  GET_SUBMISSION_DETAIL,
  GET_SUBMISSION_ENQUIRY_LIST,
  GET_SUBMISSIONS_LIST,
  GET_SUMMARY_PAYMENT_ITEMS,
  GET_SUMMARY_SEARCH,
  GET_TAX_INVOICE_STATEMENT_LIST,
  PROCEED_FOR_PAYMENT,
  RESET_AGENT_E_INVOICE_LIST,
  RESET_AGENTS_LIST,
  RESET_PAYMENT_ENQUIRY_LIST,
  RESET_SUBMISSION_ENQUIRY_LIST,
  RESET_SUBMISSIONS_LIST,
  RESET_TAX_INVOICE_STATEMENT_LIST,
  SAVE_AS_DRAFT,
  SUBMIT_PAYMENT,
  VERIFY_SUBMISSION,
} from "./e-submission.action";
import {
  CancelledPaymentRecord,
  DropdownOptions,
  EpaymentAcknowledgeResult,
  ESubmissionAgent,
  ESubmissionModel,
  ESubmissionPaymentDetails,
  ESubmissionSelectedItem,
  PaymentConfig,
  SaveDraftResult,
  SubmissionDetailResponse,
  SubmissionScreenData,
  VerifySubmissionResult,
} from "./e-submission.interface";
import { ESubmissionService } from "./e-submission.service";

// ===== State Defaults =====

const DROPDOWN_DEFAULTS: DropdownOptions = {
  productTypes: [],
  searchBy: [],
  enquirySearchBy: [],
  paymentEnquirySearchBy: [],
  paymentMode: [],
  bankNames: [],
  documentType: [],
  cbcOutstandingDays: [],
  submitStatus: [],
  paymentStatus: [],
  agentEInvoiceType: [],
  agentEInvoiceSearchBy: [],
};

@State<ESubmissionModel>({
  name: "eSubmission",
  defaults: {
    agentsList: [],
    submissionsList: [],
    enquiryList: [],
    paymentEnquiryList: [],
    agentEInvoiceList: [],
    taxInvoiceStatementList: [],
    dropdownOptions: { ...DROPDOWN_DEFAULTS },
    submissionsScreenData: null,
    paymentSubmissions: [],
    paymentDetails: null,
    paymentSourceRoute: "/e-submission/submissions",
    submissionDetail: null,
    summarySearchResult: null,
    outstandingViewData: [],
    verifyResult: null,
    isSubmissionVerified: false,
    savedDraft: null,
    cancelPaymentResult: null,
    epaymentAcknowledgement: null,
    paymentConfig: null,
  },
})
@Injectable()
export class ESubmissionState {
  constructor(private readonly eSubmissionService: ESubmissionService) {}

  // ===== Selectors: Data Lists =====

  @Selector()
  public static submissionTableData(state: ESubmissionModel): any[] {
    return state.submissionsList?.data ?? [];
  }

  @Selector()
  public static agentsList(state: ESubmissionModel): any {
    return state.agentsList ?? [];
  }

  @Selector()
  public static agentsTableData(state: ESubmissionModel): ESubmissionAgent[] {
    return state.agentsList?.data?.agentList ?? [];
  }

  @Selector()
  public static enquiryTableData(state: ESubmissionModel): any[] {
    return state.enquiryList ?? [];
  }

  @Selector()
  public static paymentEnquiryTableData(state: ESubmissionModel): any[] {
    return state.paymentEnquiryList ?? [];
  }

  @Selector()
  public static agentEInvoiceTableData(state: ESubmissionModel): any[] {
    return state.agentEInvoiceList ?? [];
  }

  @Selector()
  public static taxInvoiceStatementTableData(state: ESubmissionModel): any[] {
    return state.taxInvoiceStatementList ?? [];
  }

  // ===== Selectors: Dropdown Options =====

  @Selector()
  public static prodTypeList(state: ESubmissionModel): any[] {
    return state.dropdownOptions.productTypes;
  }

  @Selector()
  public static searchByList(state: ESubmissionModel): any[] {
    return state.dropdownOptions.searchBy;
  }

  @Selector()
  public static enquirySearchByList(state: ESubmissionModel): any[] {
    return state.dropdownOptions.enquirySearchBy;
  }

  @Selector()
  public static paymentEnquirySearchByList(state: ESubmissionModel): any[] {
    return state.dropdownOptions.paymentEnquirySearchBy;
  }

  @Selector()
  public static paymentModeList(state: ESubmissionModel): any[] {
    return state.dropdownOptions.paymentMode;
  }

  @Selector()
  public static bankNameList(state: ESubmissionModel): any[] {
    return state.dropdownOptions.bankNames;
  }

  @Selector()
  public static documentTypeList(state: ESubmissionModel): any[] {
    return state.dropdownOptions.documentType;
  }

  @Selector()
  public static cbcOutstandingDaysList(state: ESubmissionModel): any[] {
    return state.dropdownOptions.cbcOutstandingDays;
  }

  @Selector()
  public static submitStatusList(state: ESubmissionModel): any[] {
    return state.dropdownOptions.submitStatus;
  }

  @Selector()
  public static paymentStatusList(state: ESubmissionModel): any[] {
    return state.dropdownOptions.paymentStatus;
  }

  @Selector()
  public static agentEInvoiceTypeList(state: ESubmissionModel): any[] {
    return state.dropdownOptions.agentEInvoiceType;
  }

  @Selector()
  public static agentEInvoiceSearchByList(state: ESubmissionModel): any[] {
    return state.dropdownOptions.agentEInvoiceSearchBy;
  }

  // ===== Selectors: Payment Flow =====

  @Selector()
  public static submissionsScreenData(
    state: ESubmissionModel,
  ): SubmissionScreenData | null {
    return state.submissionsScreenData ?? null;
  }

  @Selector()
  public static submissionFormData(state: ESubmissionModel): any {
    return state.submissionsScreenData?.searchFormData ?? null;
  }

  @Selector()
  public static paymentSubmissions(
    state: ESubmissionModel,
  ): ESubmissionSelectedItem[] {
    return state.paymentSubmissions ?? [];
  }

  @Selector()
  public static paymentDetails(
    state: ESubmissionModel,
  ): ESubmissionPaymentDetails | null {
    return state.paymentDetails ?? null;
  }

  @Selector()
  public static paymentSourceRoute(state: ESubmissionModel): string {
    return state.paymentSourceRoute ?? "/e-submission/submissions";
  }

  // ===== Selectors: Detail & Summary =====

  @Selector()
  public static submissionDetail(
    state: ESubmissionModel,
  ): SubmissionDetailResponse | null {
    return state.submissionDetail ?? null;
  }

  // ===== Selectors: Write-side results =====

  @Selector()
  public static verifyResult(
    state: ESubmissionModel,
  ): VerifySubmissionResult | null {
    return state.verifyResult ?? null;
  }

  @Selector()
  public static isSubmissionVerified(state: ESubmissionModel): boolean {
    return state.isSubmissionVerified ?? false;
  }

  @Selector()
  public static savedDraft(state: ESubmissionModel): SaveDraftResult | null {
    return state.savedDraft ?? null;
  }

  @Selector()
  public static cancelPaymentResult(
    state: ESubmissionModel,
  ): CancelledPaymentRecord | null {
    return state.cancelPaymentResult ?? null;
  }

  @Selector()
  public static epaymentAcknowledgement(
    state: ESubmissionModel,
  ): EpaymentAcknowledgeResult | null {
    return state.epaymentAcknowledgement ?? null;
  }

  @Selector()
  public static paymentConfig(state: ESubmissionModel): PaymentConfig | null {
    return state.paymentConfig ?? null;
  }

  // ===== Actions: Submissions List =====

  @Action(GET_SUBMISSIONS_LIST)
  public getSubmissionsList(
    { patchState }: StateContext<ESubmissionModel>,
    { payload }: GET_SUBMISSIONS_LIST,
  ) {
    return this.eSubmissionService
      .getSubmissionsList(payload)
      .pipe(map((res) => patchState({ submissionsList: res ?? [] })));
  }

  @Action(RESET_SUBMISSIONS_LIST)
  public resetSubmissionsList({
    patchState,
  }: StateContext<ESubmissionModel>): void {
    patchState({ submissionsList: [] });
  }

  // ===== Actions: Payment Flow =====

  @Action(PROCEED_FOR_PAYMENT)
  public proceedForPayment(
    { patchState }: StateContext<ESubmissionModel>,
    { payload }: PROCEED_FOR_PAYMENT,
  ): void {
    const { submissionsScreenData, paymentDetails, sourceRoute } = payload;
    const selectedItems = submissionsScreenData?.selectedItems ?? [];

    patchState({
      submissionsScreenData: {
        selectedItems,
        searchFormData: submissionsScreenData?.searchFormData ?? {},
      },
      paymentSubmissions: selectedItems,
      paymentDetails,
      paymentSourceRoute: sourceRoute,
    });
  }

  @Action(SUBMIT_PAYMENT)
  public submitPayment(
    { patchState }: StateContext<ESubmissionModel>,
    { payload }: SUBMIT_PAYMENT,
  ) {
    return this.eSubmissionService.submitPayment(payload).pipe(
      map(() =>
        patchState({
          submissionsScreenData: null,
          paymentSubmissions: [],
          paymentDetails: null,
        }),
      ),
    );
  }

  // ===== Actions: Write-side =====

  @Action(VERIFY_SUBMISSION)
  public verifySubmission(
    { patchState }: StateContext<ESubmissionModel>,
    { payload }: VERIFY_SUBMISSION,
  ) {
    return this.eSubmissionService.verifySubmission(payload).pipe(
      map((res) =>
        patchState({
          verifyResult: res?.data ?? null,
          isSubmissionVerified: !!res?.data,
        }),
      ),
    );
  }

  @Action(SAVE_AS_DRAFT)
  public saveAsDraft(
    { patchState }: StateContext<ESubmissionModel>,
    { payload }: SAVE_AS_DRAFT,
  ) {
    return this.eSubmissionService
      .saveDraft(payload)
      .pipe(map((res) => patchState({ savedDraft: res?.data ?? null })));
  }

  @Action(CANCEL_SUBMISSION)
  public cancelSubmission(
    { getState, patchState }: StateContext<ESubmissionModel>,
    { payload }: CANCEL_SUBMISSION,
  ) {
    return this.eSubmissionService.cancelSubmission(payload).pipe(
      map((res) => {
        const data = res?.data;
        if (!data) {
          return;
        }
        const current = getState().submissionDetail;
        patchState({
          submissionDetail: current
            ? { ...current, ...data }
            : (data as SubmissionDetailResponse),
        });
      }),
    );
  }

  @Action(CANCEL_PAYMENT)
  public cancelPayment(
    { patchState }: StateContext<ESubmissionModel>,
    { payload }: CANCEL_PAYMENT,
  ) {
    return this.eSubmissionService
      .cancelPayment(payload)
      .pipe(
        map((res) => patchState({ cancelPaymentResult: res?.data ?? null })),
      );
  }

  @Action(COPY_SUBMISSION)
  public copySubmission(
    { patchState }: StateContext<ESubmissionModel>,
    { payload }: COPY_SUBMISSION,
  ) {
    return this.eSubmissionService.copySubmission(payload).pipe(
      map((res) => {
        const data = res?.data;
        if (data) {
          const raw: any = data.submissionsScreenData ?? {};
          data.submissionsScreenData = {
            selectedItems: raw.submissionItems ?? raw.selectedItems ?? [],
            searchFormData: raw.submissionFormData ?? raw.searchFormData ?? {},
          };
        }
        patchState({ submissionDetail: data ?? null });
      }),
    );
  }

  @Action(EPAYMENT_ACKNOWLEDGE)
  public acknowledgeEpayment(
    { patchState }: StateContext<ESubmissionModel>,
    { payload }: EPAYMENT_ACKNOWLEDGE,
  ) {
    return this.eSubmissionService
      .acknowledgeEpayment(payload)
      .pipe(
        map((res) =>
          patchState({ epaymentAcknowledgement: res?.data ?? null }),
        ),
      );
  }

  @Action(GET_PAYMENT_CONFIG)
  public getPaymentConfig({ patchState }: StateContext<ESubmissionModel>) {
    return this.eSubmissionService
      .getPaymentConfig()
      .pipe(map((res) => patchState({ paymentConfig: res?.data ?? null })));
  }

  // ===== Actions: Agents =====

  @Action(GET_AGENTS_LIST)
  public getActiveAgentsList(
    { patchState }: StateContext<ESubmissionModel>,
    { payload }: GET_AGENTS_LIST,
  ) {
    return this.eSubmissionService
      .getAgentsList(payload)
      .pipe(map((res) => patchState({ agentsList: res ?? [] })));
  }

  @Action(RESET_AGENTS_LIST)
  public resetAgentsList({ patchState }: StateContext<ESubmissionModel>): void {
    patchState({ agentsList: [] });
  }

  // ===== Actions: Dropdown Fetches =====

  private patchDropdown(
    ctx: StateContext<ESubmissionModel>,
    key: keyof DropdownOptions,
    data: any[],
  ): void {
    const dropdownOptions = { ...ctx.getState().dropdownOptions, [key]: data };
    ctx.patchState({ dropdownOptions });
  }

  @Action(GET_ESUBMISSION_PRODUCT_TYPES)
  public getProductTypes(
    ctx: StateContext<ESubmissionModel>,
    { userId }: GET_ESUBMISSION_PRODUCT_TYPES,
  ) {
    return this.eSubmissionService
      .getProductTypes(userId)
      .pipe(
        map((res) => this.patchDropdown(ctx, "productTypes", res?.data ?? [])),
      );
  }

  @Action(GET_ESUBMISSION_SEARCH_BY)
  public getSearchBy(
    ctx: StateContext<ESubmissionModel>,
    { userId }: GET_ESUBMISSION_SEARCH_BY,
  ) {
    return this.eSubmissionService
      .getSearchByOptions(userId)
      .pipe(map((res) => this.patchDropdown(ctx, "searchBy", res?.data ?? [])));
  }

  @Action(GET_ESUBMISSION_AGENT_E_INVOICE_TYPES)
  public getAgentEInvoiceTypes(
    ctx: StateContext<ESubmissionModel>,
    { userId }: GET_ESUBMISSION_AGENT_E_INVOICE_TYPES,
  ) {
    return this.eSubmissionService
      .getAgentEInvoiceTypeOptions(userId)
      .pipe(
        map((res) =>
          this.patchDropdown(ctx, "agentEInvoiceType", res?.data ?? []),
        ),
      );
  }

  @Action(GET_ESUBMISSION_AGENT_E_INVOICE_SEARCH_BY)
  public getAgentEInvoiceSearchBy(
    ctx: StateContext<ESubmissionModel>,
    { userId }: GET_ESUBMISSION_AGENT_E_INVOICE_SEARCH_BY,
  ) {
    return this.eSubmissionService
      .getAgentEInvoiceSearchByOptions(userId)
      .pipe(
        map((res) =>
          this.patchDropdown(ctx, "agentEInvoiceSearchBy", res?.data ?? []),
        ),
      );
  }

  @Action(GET_ESUBMISSION_ENQUIRY_SEARCH_BY)
  public getEnquirySearchBy(
    ctx: StateContext<ESubmissionModel>,
    { userId }: GET_ESUBMISSION_ENQUIRY_SEARCH_BY,
  ) {
    return this.eSubmissionService
      .getEnquirySearchByOptions(userId)
      .pipe(
        map((res) =>
          this.patchDropdown(ctx, "enquirySearchBy", res?.data ?? []),
        ),
      );
  }

  @Action(GET_ESUBMISSION_CBC_OUTSTANDING_DAYS)
  public getCbcOutstandingDays(
    ctx: StateContext<ESubmissionModel>,
    { userId }: GET_ESUBMISSION_CBC_OUTSTANDING_DAYS,
  ) {
    return this.eSubmissionService
      .getCbcOutstandingDaysOptions(userId)
      .pipe(
        map((res) =>
          this.patchDropdown(ctx, "cbcOutstandingDays", res?.data ?? []),
        ),
      );
  }

  @Action(GET_ESUBMISSION_SUBMIT_STATUS)
  public getSubmitStatus(
    ctx: StateContext<ESubmissionModel>,
    { userId }: GET_ESUBMISSION_SUBMIT_STATUS,
  ) {
    return this.eSubmissionService
      .getSubmitStatusOptions(userId)
      .pipe(
        map((res) => this.patchDropdown(ctx, "submitStatus", res?.data ?? [])),
      );
  }

  @Action(GET_ESUBMISSION_PAYMENT_STATUS)
  public getPaymentStatus(
    ctx: StateContext<ESubmissionModel>,
    { userId }: GET_ESUBMISSION_PAYMENT_STATUS,
  ) {
    return this.eSubmissionService
      .getPaymentStatusOptions(userId)
      .pipe(
        map((res) => this.patchDropdown(ctx, "paymentStatus", res?.data ?? [])),
      );
  }

  @Action(GET_ESUBMISSION_PAYMENT_ENQUIRY_SEARCH_BY)
  public getPaymentEnquirySearchBy(
    ctx: StateContext<ESubmissionModel>,
    { userId }: GET_ESUBMISSION_PAYMENT_ENQUIRY_SEARCH_BY,
  ) {
    return this.eSubmissionService
      .getPaymentEnquirySearchByOptions(userId)
      .pipe(
        map((res) =>
          this.patchDropdown(ctx, "paymentEnquirySearchBy", res?.data ?? []),
        ),
      );
  }

  @Action(GET_ESUBMISSION_PAYMENT_MODE)
  public getPaymentMode(
    ctx: StateContext<ESubmissionModel>,
    { userId }: GET_ESUBMISSION_PAYMENT_MODE,
  ) {
    return this.eSubmissionService
      .getPaymentModeOptions(userId)
      .pipe(
        map((res) => this.patchDropdown(ctx, "paymentMode", res?.data ?? [])),
      );
  }

  @Action(GET_ESUBMISSION_BANK_NAMES)
  public getBankNames(
    ctx: StateContext<ESubmissionModel>,
    { userId }: GET_ESUBMISSION_BANK_NAMES,
  ) {
    return this.eSubmissionService
      .getBankNameOptions(userId)
      .pipe(
        map((res) => this.patchDropdown(ctx, "bankNames", res?.data ?? [])),
      );
  }

  @Action(GET_ESUBMISSION_DOCUMENT_TYPE)
  public getDocumentType(
    ctx: StateContext<ESubmissionModel>,
    { userId }: GET_ESUBMISSION_DOCUMENT_TYPE,
  ) {
    return this.eSubmissionService
      .getDocumentTypeOptions(userId)
      .pipe(
        map((res) => this.patchDropdown(ctx, "documentType", res?.data ?? [])),
      );
  }

  // ===== Actions: Enquiry Lists =====

  @Action(GET_SUBMISSION_ENQUIRY_LIST)
  public getSubmissionEnquiryList(
    { patchState }: StateContext<ESubmissionModel>,
    { payload }: GET_SUBMISSION_ENQUIRY_LIST,
  ) {
    return this.eSubmissionService
      .getSubmissionEnquiryList(payload)
      .pipe(map((res) => patchState({ enquiryList: res?.data ?? [] })));
  }

  @Action(RESET_SUBMISSION_ENQUIRY_LIST)
  public resetSubmissionEnquiryList({
    patchState,
  }: StateContext<ESubmissionModel>): void {
    patchState({ enquiryList: [] });
  }

  @Action(GET_PAYMENT_ENQUIRY_LIST)
  public getPaymentEnquiryList(
    { patchState }: StateContext<ESubmissionModel>,
    { payload }: GET_PAYMENT_ENQUIRY_LIST,
  ) {
    return this.eSubmissionService
      .getPaymentEnquiryList(payload)
      .pipe(map((res) => patchState({ paymentEnquiryList: res?.data ?? [] })));
  }

  @Action(RESET_PAYMENT_ENQUIRY_LIST)
  public resetPaymentEnquiryList({
    patchState,
  }: StateContext<ESubmissionModel>): void {
    patchState({ paymentEnquiryList: [] });
  }

  @Action(GET_AGENT_E_INVOICE_LIST)
  public getAgentEInvoiceList(
    { patchState }: StateContext<ESubmissionModel>,
    { payload }: GET_AGENT_E_INVOICE_LIST,
  ) {
    return this.eSubmissionService
      .getAgentEInvoiceList(payload)
      .pipe(map((res) => patchState({ agentEInvoiceList: res?.data ?? [] })));
  }

  @Action(RESET_AGENT_E_INVOICE_LIST)
  public resetAgentEInvoiceList({
    patchState,
  }: StateContext<ESubmissionModel>): void {
    patchState({ agentEInvoiceList: [] });
  }

  @Action(GET_TAX_INVOICE_STATEMENT_LIST)
  public getTaxInvoiceStatementList(
    { patchState }: StateContext<ESubmissionModel>,
    { payload }: GET_TAX_INVOICE_STATEMENT_LIST,
  ) {
    return this.eSubmissionService
      .getTaxInvoiceStatementList(payload)
      .pipe(
        map((res) => patchState({ taxInvoiceStatementList: res?.data ?? [] })),
      );
  }

  @Action(RESET_TAX_INVOICE_STATEMENT_LIST)
  public resetTaxInvoiceStatementList({
    patchState,
  }: StateContext<ESubmissionModel>): void {
    patchState({ taxInvoiceStatementList: [] });
  }

  // ===== Actions: Detail & Summary =====

  @Action(GET_SUBMISSION_DETAIL)
  public getSubmissionDetail(
    { patchState }: StateContext<ESubmissionModel>,
    { submissionNo }: GET_SUBMISSION_DETAIL,
  ) {
    return this.eSubmissionService.getSubmissionDetail(submissionNo).pipe(
      map((res) => {
        const data = res?.data;
        if (data) {
          const raw = data.submissionsScreenData ?? {};
          data.submissionsScreenData = {
            selectedItems: raw.submissionItems ?? raw.selectedItems ?? [],
            searchFormData: raw.submissionFormData ?? raw.searchFormData ?? {},
          };
        }
        patchState({ submissionDetail: data ?? null });
      }),
    );
  }

  @Action(GET_SUMMARY_SEARCH)
  public getSummarySearch(
    { patchState }: StateContext<ESubmissionModel>,
    { payload }: GET_SUMMARY_SEARCH,
  ) {
    return this.eSubmissionService
      .getSummarySearch(payload)
      .pipe(map((res) => patchState({ summarySearchResult: res ?? null })));
  }

  @Action(GET_SUMMARY_PAYMENT_ITEMS)
  public getSummaryPaymentItems(
    { getState, patchState }: StateContext<ESubmissionModel>,
    { payload }: GET_SUMMARY_PAYMENT_ITEMS,
  ) {
    return this.eSubmissionService.getSummaryPaymentItems(payload).pipe(
      map((res) => {
        const searchFormData =
          getState().submissionsScreenData?.searchFormData ?? {};
        patchState({
          submissionsScreenData: {
            selectedItems: res?.data ?? [],
            searchFormData,
          },
        });
      }),
    );
  }

  @Action(GET_OUTSTANDING_VIEW)
  public getOutstandingView(
    { patchState }: StateContext<ESubmissionModel>,
    { payload }: GET_OUTSTANDING_VIEW,
  ) {
    return this.eSubmissionService
      .getOutstandingView(payload)
      .pipe(map((res) => patchState({ outstandingViewData: res?.data ?? [] })));
  }
}
